/**
 * Retry loop for a cold start that could not reach the API.
 *
 * Why this exists: `initializeAuth` runs exactly once per app launch (a
 * mount-only effect in App.js), and its offline branch used to end there —
 * the session survived, but the post-sign-in wiring (socket, plan listener,
 * the multi-device session-cap forced-logout listener, IdP linked-accounts
 * sync) never ran, and NOTHING in the app retried. The titlebar notification
 * promised "this will reconnect on its own", which was false. Worse than the
 * broken promise: `registerPlanListener` installs the session-replaced
 * eviction handler, so skipping it silently disabled a security control for
 * the whole app run.
 *
 * This module owns only the SCHEDULE — when to try again. What an attempt
 * does lives with the caller (authStore closes over `set` and the real
 * wiring); the attempt reports back one of the three outcomes below.
 *
 * Two triggers race, whichever fires first wins:
 *  - a window 'online' event (connectivity just came back — try immediately,
 *    and reset the backoff so a failure after it retries quickly again);
 *  - a capped exponential backoff timer (5s, 15s, 60s, then every 3min),
 *    because 'online' only reflects the OS's view of the local link — it
 *    never fires when the API itself was down, or behind a captive portal
 *    that just got clicked through.
 *
 * The loop is single-flight (an attempt in progress swallows both triggers)
 * and stops itself on SUCCESS or STOP. `stopAuthReconnect()` exists for the
 * session-ending paths (logout, the 401 interceptor's auth:logout, a fresh
 * browser sign-in) — a loop that outlived its session must not re-wire one.
 */

/** The attempt recovered the session and ran the wiring — loop done. */
export const AUTH_RECONNECT_SUCCESS = 'success';
/** The session is positively gone (real 401) or was ended — loop done. */
export const AUTH_RECONNECT_STOP = 'stop';
/** Still unreachable — keep the loop alive. */
export const AUTH_RECONNECT_RETRY = 'retry';

const BACKOFF_MS = [5_000, 15_000, 60_000];
const BACKOFF_CAP_MS = 180_000;

// One loop per app run, matching the one initializeAuth per app run.
let active = null;

function delayFor(attemptIndex) {
  return attemptIndex < BACKOFF_MS.length ? BACKOFF_MS[attemptIndex] : BACKOFF_CAP_MS;
}

/**
 * @param {() => Promise<'success'|'stop'|'retry'>} attempt — performs one
 *   recovery try and resolves to an AUTH_RECONNECT_* outcome. A rejection is
 *   treated as RETRY: the attempt failing is exactly the case this loop is
 *   for, never a reason to crash out of it.
 */
export function startAuthReconnect(attempt) {
  if (active) return;
  const loop = {
    attempt,
    attemptIndex: 0,
    timer: null,
    inFlight: false,
    onOnline: () => {
      loop.attemptIndex = 0;
      runAttempt(loop);
    },
  };
  active = loop;
  window.addEventListener('online', loop.onOnline);
  scheduleNext(loop);
}

export function stopAuthReconnect() {
  if (!active) return;
  if (active.timer) clearTimeout(active.timer);
  active.timer = null;
  window.removeEventListener('online', active.onOnline);
  active = null;
}

function scheduleNext(loop) {
  if (loop !== active) return;
  if (loop.timer) clearTimeout(loop.timer);
  loop.timer = setTimeout(() => {
    loop.timer = null;
    runAttempt(loop);
  }, delayFor(loop.attemptIndex));
}

function runAttempt(loop) {
  if (loop !== active || loop.inFlight) return;
  loop.inFlight = true;
  // An 'online' event may have fired while a backoff timer was pending —
  // this attempt supersedes it.
  if (loop.timer) {
    clearTimeout(loop.timer);
    loop.timer = null;
  }
  Promise.resolve()
    .then(() => loop.attempt())
    .then(
      (outcome) => finishAttempt(loop, outcome),
      () => finishAttempt(loop, AUTH_RECONNECT_RETRY)
    );
}

function finishAttempt(loop, outcome) {
  loop.inFlight = false;
  // stopAuthReconnect() ran mid-attempt (logout, sign-in) — the loop is
  // already dead; do not resurrect its timer.
  if (loop !== active) return;
  if (outcome === AUTH_RECONNECT_SUCCESS || outcome === AUTH_RECONNECT_STOP) {
    stopAuthReconnect();
    return;
  }
  loop.attemptIndex += 1;
  scheduleNext(loop);
}
